import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import {
  Activity, RefreshCw, PlayCircle, Radio, Globe, AlertCircle,
  CheckCircle2, XCircle, Clock, Webhook, Database, ExternalLink, Zap,
} from "lucide-react";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso.slice(0, 10);
  }
};

const Pill = ({ ok, children, className = "" }) => (
  <span
    className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
      ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
    } ${className}`}
  >
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {children}
  </span>
);

const IntegrationHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/integration-health`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleTest99acres = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/integration-health/99acres/test`);
      setTestResult(r.data);
      if (r.data.success) {
        toast.success(`99acres OK — fetched ${r.data.leads_found} lead(s) in last 1h`);
      } else {
        toast.error(r.data.error_message || "99acres test failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/integration-health/99acres/run-now`);
      if (r.data.success) {
        toast.success(
          `Synced — fetched ${r.data.leads_fetched || 0}, created ${r.data.leads_created || 0}`
        );
        load();
      } else {
        toast.error(r.data.error || "Sync failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setRunning(false);
    }
  };

  if (loading || !data) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center py-20 text-slate-500" data-testid="int-health-loading">
            Loading diagnostics...
          </div>
        </div>
      </Layout>
    );
  }

  const { ninetyacres, webhooks, sources, schedulers } = data;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="integration-health-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Activity className="w-6 h-6 text-violet-600" /> Integration Health
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Live status of inbound lead channels · generated {fmtTime(data.generated_at)}
            </p>
          </div>
          <Button variant="outline" onClick={load} data-testid="int-health-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* 99acres Card */}
        <Card className="border-slate-200" data-testid="card-99acres">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                  <Radio className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 flex items-center gap-2">
                    99acres Pull Sync
                    <Pill ok={ninetyacres.healthy}>{ninetyacres.healthy ? "healthy" : "attention"}</Pill>
                  </p>
                  <p className="text-xs text-slate-500">
                    Polls every 10 minutes via scheduler ·{" "}
                    Next run:{" "}
                    <span className="font-medium">{fmtTime(ninetyacres.scheduler?.next_run)}</span>
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTest99acres}
                  disabled={testing}
                  data-testid="btn-test-99acres"
                >
                  {testing ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                  Test Connection
                </Button>
                <Button
                  size="sm"
                  onClick={handleRunNow}
                  disabled={running}
                  data-testid="btn-run-99acres"
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  {running ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
                  Run Now
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat
                label="Credentials"
                value={ninetyacres.credentials_configured ? "Set ✓" : "Missing"}
                tone={ninetyacres.credentials_configured ? "ok" : "bad"}
              />
              <Stat label="Today syncs" value={`${ninetyacres.today_stats.successful_syncs}/${ninetyacres.today_stats.total_syncs}`} />
              <Stat label="Today fetched" value={ninetyacres.today_stats.leads_fetched} />
              <Stat
                label="Week failures"
                value={ninetyacres.week_stats.failed_syncs}
                tone={ninetyacres.week_stats.failed_syncs > 0 ? "bad" : "ok"}
              />
            </div>

            {ninetyacres.sync_state?.last_error && (
              <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <div className="text-xs text-rose-800">
                  <strong>Last error:</strong> {ninetyacres.sync_state.last_error}
                </div>
              </div>
            )}

            {testResult && (
              <div
                className={`flex items-start gap-2 rounded-lg p-3 border ${
                  testResult.success ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"
                }`}
                data-testid="test-result"
              >
                {testResult.success ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                )}
                <div className="text-xs">
                  <strong>{testResult.success ? "Connection OK" : "Connection failed"}</strong> —{" "}
                  {testResult.success
                    ? `${testResult.leads_found} leads in last 1h window`
                    : `${testResult.error_code || "ERROR"}: ${testResult.error_message}`}
                </div>
              </div>
            )}

            {ninetyacres.recent_logs?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-600 font-medium">
                  Recent sync logs ({ninetyacres.recent_logs.length})
                </summary>
                <div className="mt-2 space-y-1">
                  {ninetyacres.recent_logs.map((l) => (
                    <div key={l.sync_id} className="flex items-center gap-2 text-[11px] text-slate-600">
                      <Clock className="w-3 h-3" />
                      <span className="w-32">{fmtTime(l.started_at)}</span>
                      <Pill ok={l.success}>{l.success ? "ok" : "fail"}</Pill>
                      <span>fetched={l.leads_fetched}</span>
                      <span>created={l.leads_created}</span>
                      {l.error_message && <span className="text-rose-700 truncate">{l.error_message}</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </CardContent>
        </Card>

        {/* Webhooks Card */}
        <Card className="border-slate-200" data-testid="card-webhooks">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Webhook className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 flex items-center gap-2">
                    Webhook Integrations
                    <Pill ok={webhooks.healthy}>
                      {webhooks.healthy ? "active" : webhooks.active_keys ? "silent" : "no keys"}
                    </Pill>
                  </p>
                  <p className="text-xs text-slate-500">
                    Website / Meta / Google Ads / Property Portals POST to{" "}
                    <code className="text-slate-700 bg-slate-100 px-1 rounded">/api/leads/webhook</code>
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => (window.location.href = "/webhook-keys")}
                data-testid="btn-manage-keys"
              >
                Manage keys <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Active keys" value={`${webhooks.active_keys}/${webhooks.total_keys}`} tone={webhooks.active_keys ? "ok" : "bad"} />
              <Stat label="Leads (7d)" value={webhooks.webhook_leads_7d} tone={webhooks.webhook_leads_7d > 0 ? "ok" : "warn"} />
              <Stat label="Leads (30d)" value={webhooks.webhook_leads_30d} />
              <Stat label="Last webhook hit" value={fmtTime(webhooks.last_webhook_lead?.created_at)} />
            </div>

            {webhooks.keys?.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-semibold text-slate-600">API keys</p>
                <div className="space-y-1">
                  {webhooks.keys.map((k) => (
                    <div
                      key={k.key_id}
                      className="flex items-center justify-between gap-2 text-[11px] bg-slate-50 rounded-lg p-2"
                      data-testid={`webhook-key-row-${k.key_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Pill ok={!!k.active}>{k.active ? "active" : "revoked"}</Pill>
                        <span className="font-medium text-slate-800 truncate">{k.name}</span>
                        <span className="text-slate-500 font-mono text-[10px]">
                          {(k.key || "").slice(0, 16)}…
                        </span>
                        {k.default_source && (
                          <Badge variant="outline" className="text-[10px] h-4">
                            {k.default_source}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-slate-500 shrink-0">
                        <span>uses: {k.usage_count || 0}</span>
                        <span>last: {fmtTime(k.last_used_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {webhooks.recent_leads?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-600 font-medium">
                  Recent webhook leads ({webhooks.recent_leads.length})
                </summary>
                <div className="mt-2 space-y-1">
                  {webhooks.recent_leads.map((l) => (
                    <div key={l.lead_id} className="flex items-center gap-3 text-[11px] text-slate-600">
                      <span className="w-24">{fmtTime(l.created_at)}</span>
                      <Badge variant="outline" className="text-[10px] h-4">
                        {l.source}
                      </Badge>
                      <span className="truncate flex-1">{l.name}</span>
                      <span className="text-slate-400 font-mono">••{l.phone_last4}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </CardContent>
        </Card>

        {/* Source Breakdown */}
        <Card className="border-slate-200" data-testid="card-sources">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-500 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Lead Source Breakdown</p>
                <p className="text-xs text-slate-500">
                  Counts by source over the last 7 and 30 days · reveals silent channels
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2">Source</th>
                    <th className="text-right py-2 w-20">7d</th>
                    <th className="text-right py-2 w-20">30d</th>
                    <th className="text-left py-2 w-40">Last lead</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.rows.map((r) => {
                    const silent7 = r.count_7d === 0;
                    const silent30 = r.count_30d === 0;
                    const rowDimmed = silent30 ? "text-slate-400" : "";
                    return (
                      <tr key={r.source} className={`border-b border-slate-100 ${rowDimmed}`} data-testid={`source-row-${r.source}`}>
                        <td className="py-1.5 font-medium">{r.source}</td>
                        <td className={`py-1.5 text-right ${silent7 && !silent30 ? "text-amber-600 font-semibold" : ""}`}>
                          {r.count_7d}
                        </td>
                        <td className="py-1.5 text-right">{r.count_30d}</td>
                        <td className="py-1.5">{fmtTime(r.last_lead_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Schedulers */}
        <Card className="border-slate-200" data-testid="card-schedulers">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Background Schedulers</p>
                <p className="text-xs text-slate-500">Active cron jobs running on the backend</p>
              </div>
            </div>
            <div className="space-y-1 text-xs">
              {schedulers.schedulers?.map((s) => (
                <div key={s.id || s.name} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2">
                  <Pill ok={!!s.running}>{s.running ? "running" : "stopped"}</Pill>
                  <span className="font-medium text-slate-800 flex-1">{s.name}</span>
                  <span className="text-slate-500">next: {fmtTime(s.next_run)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

const Stat = ({ label, value, tone = "default" }) => {
  const toneCls =
    tone === "ok"
      ? "text-emerald-700"
      : tone === "bad"
      ? "text-rose-700"
      : tone === "warn"
      ? "text-amber-700"
      : "text-slate-800";
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <p className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${toneCls}`}>{value}</p>
    </div>
  );
};

export default IntegrationHealthPage;
